import { useState } from 'react';
import { Copy, Check, Download, FileJson, ClipboardCopy } from 'lucide-react';
import type { ColorResult } from '../lib/colorEngine';

interface ColorSpecSheetProps {
  result: ColorResult;
}

function fmt(n: number, d = 2): string {
  return Number(n.toFixed(d)).toString();
}

function contrastRatio(hex: string, bg: string): number {
  const toLum = (h: string) => {
    const c = h.replace('#', '');
    const r = parseInt(c.slice(0, 2), 16) / 255;
    const g = parseInt(c.slice(2, 4), 16) / 255;
    const b = parseInt(c.slice(4, 6), 16) / 255;
    const toL = (v: number) => v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
    return 0.2126 * toL(r) + 0.7152 * toL(g) + 0.0722 * toL(b);
  };
  const l1 = toLum(hex);
  const l2 = toLum(bg);
  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);
  return (lighter + 0.05) / (darker + 0.05);
}

function wcagLabel(ratio: number): { text: string; pass: boolean } {
  if (ratio >= 7) return { text: 'AAA', pass: true };
  if (ratio >= 4.5) return { text: 'AA', pass: true };
  if (ratio >= 3) return { text: 'AA Large', pass: true };
  return { text: 'Fail', pass: false };
}

export function ColorSpecSheet({ result }: ColorSpecSheetProps) {
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const copy = (text: string, field: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 1500);
  };

  const contrastWhite = contrastRatio(result.hex, '#ffffff');
  const contrastBlack = contrastRatio(result.hex, '#000000');
  const wcagWhite = wcagLabel(contrastWhite);
  const wcagBlack = wcagLabel(contrastBlack);

  const formatEntries: { label: string; value: string; key: string }[] = [
    { label: 'HEX', value: result.hex, key: 'hex' },
    { label: 'RGB', value: `rgb(${result.rgb.r}, ${result.rgb.g}, ${result.rgb.b})`, key: 'rgb' },
    { label: 'HSL', value: `hsl(${fmt(result.hsl.h, 0)}, ${fmt(result.hsl.s, 0)}%, ${fmt(result.hsl.l, 0)}%)`, key: 'hsl' },
    { label: 'OKLCH', value: `oklch(${fmt(result.oklch.l, 3)} ${fmt(result.oklch.c, 3)} ${fmt(result.oklch.h, 1)})`, key: 'oklch' },
    { label: 'OKLab', value: `oklab(${fmt(result.oklab.l, 3)} ${fmt(result.oklab.a, 3)} ${fmt(result.oklab.b, 3)})`, key: 'oklab' },
    { label: 'CIELAB', value: `lab(${fmt(result.lab.l)} ${fmt(result.lab.a)} ${fmt(result.lab.b)})`, key: 'lab' },
    { label: 'CMYK', value: `cmyk(${fmt(result.cmyk.c)}%, ${fmt(result.cmyk.m)}%, ${fmt(result.cmyk.y)}%, ${fmt(result.cmyk.k)}%)`, key: 'cmyk' },
    { label: 'XYZ', value: `xyz(${fmt(result.xyz.x, 4)} ${fmt(result.xyz.y, 4)} ${fmt(result.xyz.z, 4)})`, key: 'xyz' },
  ];

  if (result.displayP3) {
    formatEntries.push({ label: 'Display P3', value: result.displayP3, key: 'p3' });
  }

  const specJson = {
    hex: result.hex,
    rgb: result.rgb,
    hsl: result.hsl,
    oklch: result.oklch,
    oklab: result.oklab,
    lab: result.lab,
    cmyk: result.cmyk,
    xyz: result.xyz,
    nearestNamed: result.nearestNamed ? {
      name: result.nearestNamed.name,
      hex: result.nearestNamed.hex,
      deltaE: result.nearestNamed.deltaE,
    } : null,
    gamut: result.inGamut,
    contrast: {
      onWhite: { ratio: parseFloat(contrastWhite.toFixed(2)), wcag: wcagWhite.text },
      onBlack: { ratio: parseFloat(contrastBlack.toFixed(2)), wcag: wcagBlack.text },
    },
  };

  const summaryText = [
    `Color: ${result.hex}`,
    result.nearestNamed ? `Nearest: ${result.nearestNamed.name} (${result.nearestNamed.hex}, ΔE ${fmt(result.nearestNamed.deltaE)})` : '',
    `RGB: ${result.rgb.r}, ${result.rgb.g}, ${result.rgb.b}`,
    `HSL: ${fmt(result.hsl.h, 0)}° ${fmt(result.hsl.s, 0)}% ${fmt(result.hsl.l, 0)}%`,
    `OKLCH: ${fmt(result.oklch.l, 3)} ${fmt(result.oklch.c, 3)} ${fmt(result.oklch.h, 1)}`,
    `CMYK: ${fmt(result.cmyk.c)}% ${fmt(result.cmyk.m)}% ${fmt(result.cmyk.y)}% ${fmt(result.cmyk.k)}%`,
    `Gamut: sRGB=${result.inGamut.srgb ? '✓' : '✗'} P3=${result.inGamut.p3 ? '✓' : '✗'} Rec.2020=${result.inGamut.rec2020 ? '✓' : '✗'}`,
    `Contrast on white: ${contrastWhite.toFixed(2)}:1 (${wcagWhite.text})`,
    `Contrast on black: ${contrastBlack.toFixed(2)}:1 (${wcagBlack.text})`,
  ].filter(Boolean).join('\n');

  const downloadJson = () => {
    const blob = new Blob([JSON.stringify(specJson, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `color-spec-${result.hex.replace('#', '')}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--s-6)' }}>

      {/* ── Large Swatch ────────────────────────────────── */}
      <section className="cmp-section" style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{
          height: 160, background: result.hex,
          display: 'flex', alignItems: 'flex-end', padding: 'var(--s-4)',
        }}>
          <span style={{
            fontFamily: 'var(--font-mono)', fontSize: 'var(--t-h2)',
            fontWeight: 'var(--w-bold)' as unknown as number,
            color: contrastWhite > contrastBlack ? '#fff' : '#000',
            textShadow: '0 1px 4px rgba(0,0,0,0.25)',
          }}>
            {result.hex.toUpperCase()}
          </span>
        </div>
        {result.nearestNamed && (
          <div style={{
            padding: 'var(--s-3) var(--s-4)',
            display: 'flex', alignItems: 'center', gap: 'var(--s-3)',
            fontSize: 'var(--t-body)', color: 'var(--ink-2)',
          }}>
            <span style={{
              width: 20, height: 20, borderRadius: 'var(--radius-sm)',
              background: result.nearestNamed.hex,
              border: '1px solid var(--line)',
              flexShrink: 0,
            }} />
            <span>
              Nearest: <strong style={{ color: 'var(--ink)' }}>{result.nearestNamed.name}</strong>
              {' '}({result.nearestNamed.hex}) · ΔE {fmt(result.nearestNamed.deltaE)}
            </span>
          </div>
        )}
      </section>

      {/* ── All Formats ─────────────────────────────────── */}
      <section className="cmp-section">
        <h3 className="cmp-metric-section__title" style={{ marginBottom: 'var(--s-3)' }}>Color Values</h3>
        <div className="grid-2" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 'var(--s-2)' }}>
          {formatEntries.map(({ label, value, key }) => (
            <button
              key={key}
              type="button"
              onClick={() => copy(value, key)}
              style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: 'var(--s-2) var(--s-3)',
                background: 'var(--panel-2)', border: '1px solid var(--line)',
                borderRadius: 'var(--radius-md)', cursor: 'pointer',
                fontFamily: 'inherit', fontSize: 'var(--t-caption)',
                color: 'var(--ink)', textAlign: 'left',
                transition: 'border-color 100ms',
              }}
              title={copiedField === key ? 'Copied ✓' : `Copy ${label}`}
            >
              <span>
                <span style={{ color: 'var(--ink-3)', fontWeight: 600, marginRight: 'var(--s-2)' }}>{label}</span>
                <code style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--t-caption)' }}>{value}</code>
              </span>
              {copiedField === key
                ? <Check size={13} style={{ color: 'var(--semantic-pass, #16a34a)', flexShrink: 0 }} />
                : <Copy size={13} style={{ color: 'var(--ink-3)', flexShrink: 0 }} />
              }
            </button>
          ))}
        </div>
      </section>

      {/* ── Gamut ───────────────────────────────────────── */}
      <section className="cmp-section">
        <h3 className="cmp-metric-section__title" style={{ marginBottom: 'var(--s-3)' }}>Gamut Coverage</h3>
        <div style={{ display: 'flex', gap: 'var(--s-3)', flexWrap: 'wrap' }}>
          {[
            { label: 'sRGB', inGamut: result.inGamut.srgb },
            { label: 'Display P3', inGamut: result.inGamut.p3 },
            { label: 'Rec.2020', inGamut: result.inGamut.rec2020 },
          ].map(({ label, inGamut }) => (
            <div key={label} style={{
              display: 'flex', alignItems: 'center', gap: 'var(--s-2)',
              padding: 'var(--s-2) var(--s-3)',
              background: 'var(--panel-2)', borderRadius: 'var(--radius-md)',
              border: '1px solid var(--line)',
              fontSize: 'var(--t-caption)',
            }}>
              <span style={{
                width: 8, height: 8, borderRadius: '50%',
                background: inGamut ? 'var(--semantic-pass, #16a34a)' : 'var(--semantic-fail, #dc2626)',
              }} />
              <span style={{ fontWeight: 600, color: 'var(--ink)' }}>{label}</span>
              <span style={{ color: 'var(--ink-3)' }}>{inGamut ? 'In gamut' : 'Out of gamut'}</span>
            </div>
          ))}
        </div>
      </section>

      {/* ── Contrast ────────────────────────────────────── */}
      <section className="cmp-section">
        <h3 className="cmp-metric-section__title" style={{ marginBottom: 'var(--s-3)' }}>WCAG Contrast</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--s-3)' }}>
          {[
            { label: 'On White', bg: '#ffffff', ratio: contrastWhite, wcag: wcagWhite },
            { label: 'On Black', bg: '#000000', ratio: contrastBlack, wcag: wcagBlack },
          ].map(({ label, bg, ratio, wcag }) => (
            <div key={label} style={{
              borderRadius: 'var(--radius-lg)', overflow: 'hidden',
              border: '1px solid var(--line)',
            }}>
              <div style={{
                background: bg, padding: 'var(--s-4)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                minHeight: 56,
              }}>
                <span style={{
                  color: result.hex, fontWeight: 700,
                  fontSize: 'var(--t-body-lg)',
                }}>Aa</span>
              </div>
              <div style={{
                padding: 'var(--s-2) var(--s-3)',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                fontSize: 'var(--t-caption)', background: 'var(--panel-2)',
              }}>
                <span style={{ color: 'var(--ink-2)' }}>{label}</span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 'var(--s-2)' }}>
                  <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 600, color: 'var(--ink)' }}>
                    {ratio.toFixed(2)}:1
                  </span>
                  <span style={{
                    padding: '1px 6px', borderRadius: '9999px',
                    fontSize: 10, fontWeight: 700,
                    background: wcag.pass ? 'oklch(0.92 0.04 145)' : 'oklch(0.92 0.04 25)',
                    color: wcag.pass ? 'var(--semantic-pass, #16a34a)' : 'var(--semantic-fail, #dc2626)',
                  }}>
                    {wcag.text}
                  </span>
                </span>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Export Actions ───────────────────────────────── */}
      <section style={{
        display: 'flex', gap: 'var(--s-2)', flexWrap: 'wrap',
        paddingTop: 'var(--s-2)',
      }}>
        <button
          type="button"
          onClick={() => copy(JSON.stringify(specJson, null, 2), 'json')}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 'var(--s-2)',
            padding: 'var(--s-2) var(--s-4)', fontSize: 'var(--t-body)',
            fontWeight: 600, fontFamily: 'inherit',
            background: 'var(--panel)', border: '1px solid var(--line)',
            borderRadius: 'var(--radius-lg)', cursor: 'pointer',
            color: 'var(--ink)', transition: 'background 100ms',
          }}
        >
          {copiedField === 'json'
            ? <><Check size={15} style={{ color: 'var(--semantic-pass)' }} /> Copied</>
            : <><FileJson size={15} /> Copy as JSON</>
          }
        </button>
        <button
          type="button"
          onClick={downloadJson}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 'var(--s-2)',
            padding: 'var(--s-2) var(--s-4)', fontSize: 'var(--t-body)',
            fontWeight: 600, fontFamily: 'inherit',
            background: 'var(--accent)', border: 'none',
            borderRadius: 'var(--radius-lg)', cursor: 'pointer',
            color: 'white', transition: 'filter 100ms',
          }}
        >
          <Download size={15} /> Download .json
        </button>
        <button
          type="button"
          onClick={() => copy(summaryText, 'summary')}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 'var(--s-2)',
            padding: 'var(--s-2) var(--s-4)', fontSize: 'var(--t-body)',
            fontWeight: 600, fontFamily: 'inherit',
            background: 'var(--panel)', border: '1px solid var(--line)',
            borderRadius: 'var(--radius-lg)', cursor: 'pointer',
            color: 'var(--ink)', transition: 'background 100ms',
          }}
        >
          {copiedField === 'summary'
            ? <><Check size={15} style={{ color: 'var(--semantic-pass)' }} /> Copied</>
            : <><ClipboardCopy size={15} /> Copy summary</>
          }
        </button>
      </section>
    </div>
  );
}
